import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select } from 'antd'
import CustomBreadcrumb from '../../common/CustomBreadcrumb'
import CommonTable from '../../common/CommonTable';
import {
    aiCampaignsColumns,
    CAMPAIGN_TYPE_FILTER_OPTIONS,
    isWelcomeOnboardingCampaign,
} from './aiCampaignsUtils';
import { ThemeContext } from '../../common/themeContext';
import Loader from '../Loder/loader';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getAllAiCampaignsHandler } from '../../redux/action/getAllAiCampaignsSlice';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { ChevronIcon } from '../../assets/icon';

const AiCampaigns = () => {
    const navigate = useNavigate();
    const {
        canEditCampaigns,
        hasCampaignViewOnly,
        isSuperAdmin,
        PERMISSIONS,
        hasPermission,
    } = useAdminPermissions();

    const showManageCampaign =
        isSuperAdmin || hasPermission(PERMISSIONS.CAMPAIGN_EDIT);
    const showViewCampaign =
        hasCampaignViewOnly ||
        (hasPermission(PERMISSIONS.CAMPAIGN_VIEW) && !showManageCampaign);

    const columns = aiCampaignsColumns(navigate, {
        showManage: showManageCampaign,
        showView: showViewCampaign,
    });

    const { brandId } = useContext(ThemeContext);
    const dispatch = useDispatch();
    const getAllAiCampaignsState = useSelector((state) => state?.getAllAiCampaigns);
    console.log("getAllAiCampaignsState", getAllAiCampaignsState);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignType, setCampaignType] = useState("ALL");
    const [campaignTypeOpen, setCampaignTypeOpen] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: [5, 10, 15, 20],
        className: "order-pagination",
        locale: { items_per_page: "" },
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
    });

	const normalizeCampaignStatus = (status) => {
        if (!status) return status;
        if (typeof status !== 'string') return status;

        const map = {
            HOLD: "Hold",
            ACTIVE: "Active",
            ARCHIVE: "Archive",
            DRAFT: "Draft",
        };

        const trimmed = status.trim();
        if (map[trimmed]) return map[trimmed];

        // Fallback: turn "hold" -> "Hold", "ACTIVE" -> "Active"
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };

    const normalizeCampaignDuration = (item) => {
        // Prefer backend-provided duration if present.
        if (item?.campaignDuration) return item.campaignDuration;

        const start = item?.campaignStartDate;
        const end = item?.campaignEndDate;
        const isWelcome = isWelcomeOnboardingCampaign(item?.campaignType);

        if (start && isWelcome) {
            return `${moment(start).format('MM/DD/YYYY')} - Ongoing`;
        }
        if (start && end) {
            const startFormatted = moment(start).format('MM/DD/YYYY');
            const endFormatted = moment(end).format('MM/DD/YYYY');
            return `${startFormatted} - ${endFormatted}`;
        }
        return '';
    };

	const listPayload = useMemo(() => {
        return {
            refBrandId: brandId,
            campaignType: !campaignType || campaignType === "ALL" ? null : campaignType,
            status: null,
        };
    }, [brandId, campaignType]);

    const fetchCampaigns = (page = 1, pageSize = pagination.pageSize) => {
        if (!brandId) return;
        window.scrollTo(0, 0);
        let payload = {
            ...listPayload,
            offSet: page,
            limit: pageSize,
        };
        console.log("payload", payload);
        dispatch(getAllAiCampaignsHandler(payload));
    };

    useEffect(() => {
        if (!brandId) return;
        fetchCampaigns(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId, campaignType]);

    useEffect(() => {
        const items = getAllAiCampaignsState?.data?.data;
        if (!items) return;

        const mapped = items.map((item) => ({
            // Keep key for antd Table stability; fall back to campaignName.
            key: item?.id ?? item?.campaignId ?? item?.campaignName,
            campaignName: item?.campaignName ?? item?.name ?? "",
            campaignType: item?.campaignType ?? item?.type ?? "",
            campaignDuration: normalizeCampaignDuration(item),
            status: normalizeCampaignStatus(item?.status),
            ...item,
        }));

        setCampaigns(mapped);

        setPagination((prev) => ({
            ...prev,
            total: getAllAiCampaignsState?.data?.totalElements ?? 0,
            current: getAllAiCampaignsState?.data?.pageNumber ?? 1	,
            pageSize: getAllAiCampaignsState?.data?.pageSize ?? prev.pageSize,
        }));
    }, [getAllAiCampaignsState]);

    const addProduct = () => {
        navigate("/ai-campaigns/campaign-details", { state: null });
    };

    const handleCampaignTypeChange = (value) => {
        setCampaignType(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handlePageChange = (page, pageSize) => {
        fetchCampaigns(page, pageSize);
    };

    return (
        <div className="rightside">
            <div className="rightcontent">
                <p>
                    <strong>Campaign List</strong>
                </p>
                <CustomBreadcrumb
                    items={[{ text: "Home" }, { text: "AI Campaigns" }, { text: "Campaign List", active: true }]}
                />

                <div className="table-padding">
                    <div className="headtitle">
                        <p className="mb-0 heedprodct">Campaign List</p>
                        <div className="d-flex align-items-center gap-3">
                            <div className="customSelectInput mb-0" style={{ minWidth: 220 }}>
                                <Select
                                    className="customSelect w-100"
                                    placeholder="Campaign type"
                                    value={campaignType}
                                    onChange={handleCampaignTypeChange}
                                    onOpenChange={setCampaignTypeOpen}
                                    options={CAMPAIGN_TYPE_FILTER_OPTIONS}
                                    suffixIcon={<ChevronIcon isOpen={campaignTypeOpen} />}
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                            {canEditCampaigns && (
                                <button className="primarybtn" type="button" onClick={addProduct}>
                                    <div className="plus"></div>
                                    Create Enrollment Campaign
                                </button>
                            )}
                        </div>
                    </div>
                    <hr className="m-0" />
                    <div className="table-responsive">
                        {getAllAiCampaignsState?.isLoading === true && (
                            <Loader loaderTransform="loaderTransform" />
                        )}
                        <CommonTable
                            handlePageChange={handlePageChange}
                            pagination={pagination}
                            body={campaigns}
                            columns={columns}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AiCampaigns
